import moment from "moment";
import "./customMoment";
import {ODATA_COLUMN_TYPES} from "./odata";

/* ==========================================================================
   NUMERIC FORMATTERS & CONSTANTS
   ========================================================================== */

export const numberFormatter = new Intl.NumberFormat().format;

export const DECIMAL_SEPARATOR_DEFAULT = ",";
export const DECIMAL_PLACES_DEFAULT = 2;
export const TABLE_EMPTY_CHAR_DEFAULT = "";
export const ROUNDING_STRATEGY = "NormalRounding";

/**
 * Returns the corresponding rounding mode behavior for Intl.NumberFormat based on custom config.
 *
 * @param {string} roundingStrategy - The rounding strategy label.
 * @returns {string|undefined} Decimal rounding mode identifier or undefined.
 */
const getRoundingMode = roundingStrategy => {
  if (roundingStrategy === "RoundingUp") {
    return "ceil";
  } else if (roundingStrategy === "Truncation") {
    return "floor";
  } else {
    return undefined;
  }
};

/**
 * Formats a numeric value with custom decimal options, grouping, separator, and rounding.
 *
 * @param {number|string} value - The numeric value to format.
 * @param {string} decimalSeparator - Character used to separate decimal digits.
 * @param {number} decimalPlaces - Desired number of decimal digits.
 * @param {string} emptyChar - Default fallback string when value is empty or nil.
 * @param {string} roundingStrategy - Selection between NormalRounding, RoundingUp, or Truncation.
 * @param {boolean} stripDecimalsIfInteger - Removes decimals if the final result is an integer.
 * @returns {string|number} Formatted local string representation.
 */
export const getFormattedValue = (
  value,
  decimalSeparator = DECIMAL_SEPARATOR_DEFAULT,
  decimalPlaces = DECIMAL_PLACES_DEFAULT,
  emptyChar = TABLE_EMPTY_CHAR_DEFAULT,
  roundingStrategy = ROUNDING_STRATEGY,
  stripDecimalsIfInteger = true
) => {
  if (value === null || value === undefined || (typeof value === "string" && value.length === 0)) {
    return emptyChar;
  }

  try {
    let n = value;

    if (typeof value === "string") {
      const regex = /^[-+]?\d+(?:[.,]\d+)?$/;
      if (!regex.test(value)) return value;
      n = Number(value.replace(",", "."));
    }

    if (typeof n !== "number" || !Number.isFinite(n)) {
      return value;
    }

    const nf = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimalPlaces < 0 ? 0 : decimalPlaces,
      maximumFractionDigits: decimalPlaces < 0 ? 100 : decimalPlaces,
      trailingZeroDisplay: stripDecimalsIfInteger ? "stripIfInteger" : "auto",
      roundingMode: getRoundingMode(roundingStrategy),
      useGrouping: "always"
    });

    const groupSeparator = decimalSeparator === "." ? "," : ".";

    const parts = nf.formatToParts(n);
    return parts
      .map(p => {
        if (p.type === "decimal") return decimalSeparator;
        if (p.type === "group") return groupSeparator;
        return p.value;
      })
      .join("");
  } catch (e) {
    return value;
  }
};

/* ==========================================================================
   TEMPORAL DIMENSION FORMATTERS
   ========================================================================== */

const TEMPORAL_TYPES = new Set([
  ODATA_COLUMN_TYPES.dateTime,
  ODATA_COLUMN_TYPES.time,
  ODATA_COLUMN_TYPES.day,
  ODATA_COLUMN_TYPES.month,
  ODATA_COLUMN_TYPES.year,
  ODATA_COLUMN_TYPES.quarter,
  ODATA_COLUMN_TYPES.quadrimester,
  ODATA_COLUMN_TYPES.semester
]);

/**
 * Checks if a dimension type is a temporal (time-related) type.
 *
 * @param {string} type - The column type to check.
 * @returns {boolean} True if it's a temporal type.
 */
export const isTemporalDimensionType = type => TEMPORAL_TYPES.has(type);

/**
 * Formats a temporal value based on its type and a provided format string.
 *
 * @param {string|number} value - Raw temporal value from the data source.
 * @param {string} type - The column type (e.g. YEAR, QUARTER, TIME).
 * @param {string} format - The moment-compatible format string.
 * @returns {string|null} Formatted string or null if input is invalid or not temporal.
 */
export const formatTemporalValue = (value, type, format) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (!isTemporalDimensionType(type)) {
    return null;
  }

  // Return a plain string fallback if there is no format configured,
  // or if we receive a raw number for non-YEAR types to prevent moment
  // from misinterpreting it as milliseconds since the Unix Epoch.
  if (!format || (typeof value === "number" && type !== ODATA_COLUMN_TYPES.year)) {
    return String(value);
  }

  let m;
  switch (type) {
    case ODATA_COLUMN_TYPES.year: {
      // OData compute expressions can return a year as a raw number or a decimal
      // (for example 2024 or 2024.0). Normalize those values back to an integer
      // first so we still apply the configured temporal formatting instead of
      // letting Moment treat them as generic numeric input.
      const numericYearPattern = /^[-+]?\d+(?:\.\d+)?$/;
      const numericYear =
        typeof value === "number"
          ? Math.trunc(value)
          : typeof value === "string" && numericYearPattern.test(value.trim())
            ? Math.trunc(Number(value))
            : null;

      m = numericYear !== null ? moment(String(numericYear), "YYYY", true) : moment(value);
      break;
    }
    case ODATA_COLUMN_TYPES.time: {
      m = moment(`1970-01-01T${value}`);
      break;
    }
    case ODATA_COLUMN_TYPES.quarter:
    case ODATA_COLUMN_TYPES.quadrimester:
    case ODATA_COLUMN_TYPES.semester: {
      m = moment().customParse(value);
      break;
    }
    default: {
      m = moment(value);
      break;
    }
  }

  if (m && m.isValid()) {
    return m.customFormat(format);
  }

  return String(value);
};

/* ==========================================================================
   GENERIC STRING HANDLERS
   ========================================================================== */

/**
 * Strips whitespace characters from both ends of the string.
 * Supports a polyfill fallback if build runtimes lack String.prototype.trim.
 *
 * @param {string} str - Input source string.
 * @returns {string} Trimmed string.
 */
export const getTrimmedStr = str => {
  if (!str || str.length === 0) {
    return "";
  }

  if (String.prototype.trim) {
    return str.trim();
  } else {
    return str.replace(/^\s+|\s+$/gm, "");
  }
};

/**
 * Capitalizes the first character in a string.
 *
 * @param {string} string - Input source string.
 * @returns {string} String with the first letter capitalized.
 */
export const toUpperCaseFirst = string =>
  string !== null && string !== undefined && string.length > 0 ? string[0].toUpperCase() + string.slice(1) : string;

/* ==========================================================================
   TIME SPAN DURATION FORMATTERS (SECONDS TO HMS)
   ========================================================================== */

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;

/**
 * Transforms a duration in seconds into a well-constructed "Xd Xh Xm Xs" representation.
 * Handles sub-zero exceptions representing long-duration valid indicators.
 *
 * @param {number} days - Days component.
 * @param {number} hours - Hours component.
 * @param {number} mins - Minutes component.
 * @param {number} sec - Seconds component.
 * @returns {string} Formatted duration string.
 */
const wellFormattedTimeString = (days, hours, mins, sec) => {
  let result = "";
  result += days > 0 ? days + "d " : ""; // hide days if 0
  result += (hours < 10 ? " " + hours : hours) + "h ";
  result += (mins < 10 ? " " + mins : mins) + "m ";

  if (days === 0) {
    // truncate if days is greater than zero
    result += (sec < 10 ? " " + sec : sec) + "s";
  }

  return result;
};

/**
 * Converts seconds into custom human-readable localized HH:MM:SS layout.
 *
 * @param {number} totalSeconds - Numeric amount of seconds.
 * @param {function} t - Translation trigger handler.
 * @returns {string} Localized duration text.
 */
export const secondsToHms = (totalSeconds, t) => {
  if (totalSeconds < 0) {
    return t("scenes.nodeSettings.cacheSettings.alwaysValid");
  } else if (totalSeconds === 0) {
    return t("scenes.nodeSettings.cacheSettings.disabled");
  } else {
    const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
    const hours = Math.floor((totalSeconds - days * SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    const minutes = Math.floor((totalSeconds - days * SECONDS_PER_DAY - hours * SECONDS_PER_HOUR) / 60);
    let seconds = totalSeconds - days * SECONDS_PER_DAY - hours * SECONDS_PER_HOUR - minutes * 60;

    // round seconds
    seconds = Math.round(seconds * 100) / 100;

    return wellFormattedTimeString(days, hours, minutes, seconds);
  }
};
